/* ---------------------------------------------------------------------------
 * bc-sewing-3d — the PDP Sewing section's interactive 3D pattern viewers.
 *
 * A sewn end is not an art asset: it is a handful of catalog dimensions. So
 * there is no GLB here (that is assets/bc-model-3d.js's job, and its subject is
 * a machined part). Every millimetre of what renders is BUILT at runtime from
 * the same sewing config the theme's own 2D figures parse — the
 * `script[data-bc-sewing-figure-data]` JSON that snippets/bc-sewing.liquid
 * emits (webbing visuals from bc-sewing-webbing-json + `sewing.patterns` from
 * the shared bc-sewing-patterns-json emitter, the same entries the Segment
 * Builder consumes). If the metaobject says the opening is 139 mm then the eye
 * in the picture is 139 mm of webbing, and a second product with a second
 * pattern needs no code at all: nothing in this file is product-specific.
 *
 * NOTHING IS FETCHED but three.js itself.
 *
 * HOST MARKUP CONTRACT (emitted by Liquid; see the sewing snippets):
 *
 *   <div data-bcm3d-host data-bcm3d-kind="loop|join" data-bcm3d-pattern="512">
 *     <div class="bcm3d-stage" data-bcm3d-stage>
 *       <img class="bcm3d-poster" …>        <- still of the default view
 *       <p class="bcm3d-hint">…3 spans…</p>
 *     </div>
 *     <button type="button" data-bcm3d-expand>⤢</button>
 *     <button type="button" data-bcm3d-exit>×</button>
 *   </div>
 *
 *   data-bcm3d-kind     "loop" | "join". Optional — the resolved pattern's own
 *                       `kind` is the fallback. (`data-bcm3d-model`, the
 *                       mockup's spelling, is accepted as a synonym.)
 *   data-bcm3d-pattern  the sewing_pattern's CODE ("512") or its handle; either
 *                       resolves. Optional — with no token the first pattern of
 *                       the host's kind is used. `data-bcm3d-pattern-handle`
 *                       and `data-pattern-handle` (the <bc-sewing-figure>
 *                       spelling) are accepted as synonyms.
 *
 * The canvas and the corner block are created HERE and appended to the stage;
 * everything else in the stage is Liquid's. The poster is hidden by CSS on
 * `[data-bcm3d-built]` and simply stays if three.js never arrives (offline, no
 * WebGL, a blocked CDN) — exactly the job the variant photo does under
 * bc-model-3d.js.
 *
 * THE × PUTS THE MODEL DOWN; IT DOES NOT PUT IT AWAY (Jerry 2026-08-24). The
 * poster is the pattern's FLAT MACHINE ART — a drawing of the stitching, a
 * different subject from the sewn end the panel is about — so a × that faded
 * it back over the canvas answered "I am done turning this over" with the
 * stitch drawing, which is not "back" from anywhere. The ×
 * (`data-bcm3d-exit`) and Escape (armed panels only — a page-wide key must not
 * fold up every panel scrolled past) hand back the DEFAULT VIEW instead: the
 * picture the panel showed the moment the model first appeared — home pose,
 * home distance, no pan, sleeve back on — with the gestures released, so the
 * page scrolls straight over it again (`api.reset`).
 *
 * ONE STATE FLAG, therefore: `[data-bcm3d-built]`, set when the viewer exists
 * and never removed. It fades the canvas up over the poster, brings in the
 * corner block, the ⤢ and the ×, and keeps the hint saying "Click to interact"
 * whenever the panel is not armed. (There was a second, `ready`, for as long as
 * the flat art was somewhere the reader could be sent back to.)
 *
 * WHY THREE.JS AND NOT <model-viewer>, and why 0.165 from jsDelivr `/+esm`:
 * this is the same viewer contract as assets/bc-model-3d.js and
 * tools/parts-diagram-tool/make_preview_html.py — FREE TUMBLE (drag rotates the
 * MODEL about the CAMERA's screen axes, a quaternion premultiply, so there is
 * no pole to stick at and no upright it snaps back to), wheel/pinch zoom, and
 * the inline model INERT until the reader arms it so a live camera can never
 * eat the page scroll. Change one, change all three. `/+esm` is what lets a
 * module import the addons at all: `examples/jsm/*` import the bare specifier
 * `three`, which would need an import map; `/+esm` rewrites it to the same
 * pinned URL we import ourselves, so both modules share ONE three instance.
 *
 * WHERE THE COLOURS COME FROM. Not from the sewing JSON's side-view palette
 * (two hexes and a sleeve) but from the figure the reader already scrolled past
 * in Dimensions: the theme's `bc-webbing-cross-section` SVG, drawn TO SCALE
 * from the product's `webbing_pattern` metaobject, carrying the whole design —
 * top-half colour, bottom-half colour, the four rounded-edge quadrant colours,
 * longitudinal marks, the inner edge circles and the inner pill.
 * readCrossSection() parses the VISIBLE pane out of the live DOM (products with
 * a `custom.diagram` cross_sections map render several, all but one `hidden`),
 * so the model and the diagram cannot disagree — including after
 * assets/bc-variant-recolor.js rewrites the diagram's fills on a variant change,
 * since the parse reads whatever is in the document at build time.
 *
 * MAPPING. The cross-section is an END-ON view: its x axis runs across the
 * width, its y axis through the thickness. So anything that varies across x
 * becomes a LONGITUDINAL stripe down the strand, and anything that varies
 * through y becomes top face / bottom face / edge. The two interior features
 * (the inner circles and the inner pill) are on no outer surface at all — they
 * are what you see IN a cut — so they are painted on the cut-end faces only,
 * which makes each cut end a little copy of the Dimensions figure.
 *
 * THE SECTION IS A STADIUM, NOT A BOX, because that is what a woven edge is and
 * what the cross-section draws (`rx = bar_h / 2`): flat top, flat bottom, a true
 * semicircular round at each selvage, smooth normals all the way round. A
 * stadium is tangent-continuous, so the flat/round junction has the same normal
 * on both sides and the "crease vs blend" question never arises; the colour
 * break does the edge-defining work, exactly where the diagram puts it.
 *
 * THE MODEL RENDERS CLEAN — no dimension lines, no witness lines, no badges.
 * What is in the scene is the product: the webbing with its cross-section
 * colours carried round the fold, the stitching, the sleeve. The numbers are
 * read once, at rest, in the corner block (and in full, with inch conversions
 * and real row headers, in the written rows the section already renders).
 *
 * ------------------------------------------------------------------------- */

const THREE_BASE = 'https://cdn.jsdelivr.net/npm/three@0.165.0';
const MODULE_URLS = [
  `${THREE_BASE}/+esm`,
  `${THREE_BASE}/examples/jsm/environments/RoomEnvironment.js/+esm`,
];

/* --------------------------------------------------------------- config --- */

/**
 * The page's sewing config, per host.
 *
 * Resolution mirrors <bc-sewing-figure> (assets/bc-segment-diagram.js): the
 * ENCLOSING `.bc-sewing`'s own config wins (the PDP shape, one config per
 * section), else the page's single document-level copy (the batch report page
 * emits ONE config for all its groups rather than repeating several KB of JSON
 * per group). Parsed once per script tag, however many hosts share it.
 */
const configCache = new WeakMap();

function configFor(host) {
  const holder = host.closest('.bc-sewing');
  const tag = (holder && holder.querySelector('script[data-bc-sewing-figure-data]'))
    || document.querySelector('script[data-bc-sewing-figure-data]');
  if (!tag) return null;
  if (configCache.has(tag)) return configCache.get(tag);

  let cfg = null;
  try {
    const raw = JSON.parse(tag.textContent);
    const web = raw.webbing || {};
    const patterns = (raw.sewing && raw.sewing.patterns) || [];
    // The webbing's real cross-section is what every colour and both section
    // dimensions come from; without it there is nothing to sweep.
    if (Number(web.widthMm) > 0 && Number(web.thicknessMm) > 0 && patterns.length) {
      cfg = { tag, web, patterns };
    }
  } catch (_e) {
    cfg = null;
  }
  configCache.set(tag, cfg);
  return cfg;
}

/** The number a metaobject supplied, or 0 — every dimension is guarded. */
const mmOf = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** First number in an authored dimension string ("139 mm" -> 139). */
const mmText = (s) => {
  const m = /(-?[\d.]+)/.exec(String(s || ''));
  return m ? mmOf(m[1]) : 0;
};

/**
 * Which pattern this host draws, and in which kind.
 *
 * A code ("512") or a handle both resolve, because the theme identifies a
 * pattern by handle (<bc-sewing-figure data-pattern-handle>) and the reader
 * identifies it by code (the panel eyebrow, the written rows, the machine
 * file). With no token at all the first pattern of the host's kind is taken,
 * which is what a single-pattern webbing wants.
 */
function patternFor(cfg, host) {
  const d = host.dataset;
  const token = String(d.bcm3dPattern || d.bcm3dPatternHandle || d.patternHandle || '').trim();
  const asked = String(d.bcm3dKind || d.bcm3dModel || '').trim().toLowerCase();
  const kindOf = (p) => String((p && p.kind) || 'loop').toLowerCase();

  let pat = null;
  if (token) {
    // Both sides trimmed: the host's token is Liquid-`strip`ped, the JSON's
    // code/handle carry whatever the metaobject field holds.
    pat = cfg.patterns.find((p) => String(p.code || '').trim() === token)
      || cfg.patterns.find((p) => String(p.handle || '').trim() === token)
      || null;
  }
  if (!pat && asked) pat = cfg.patterns.find((p) => kindOf(p) === asked) || null;
  if (!pat) pat = cfg.patterns[0] || null;
  if (!pat) return null;

  // Only two kinds have a 3D model. Anything else (an authored kind we have no
  // geometry for) reads as a loop, the way the 2D figures default.
  const kind = (asked || kindOf(pat)) === 'join' ? 'join' : 'loop';
  return { pat, kind };
}

/* ------------------------------------------------------------ colour ------ */

/**
 * A hex the metaobject supplied, or the fallback — the ONLY validation the
 * thread colours get here.
 *
 * assets/bc-segment-diagram.js runs them through `contrastInk` first, which
 * keeps the hue and pushes it until it reads 2.2:1 against the webbing. That is
 * the right answer on a flat SVG fill and the wrong one here: it turns a red
 * thread on a purple webbing into a pale pink, and in a lit 3D scene the thread
 * is already separated from the webbing by its own shading and its own relief.
 * Real thread, real colour.
 */
const NON_COLOUR_KEYWORDS = new Set(['none', 'transparent', 'inherit', 'initial', 'unset', 'currentcolor']);

const hex = (raw, fallback) => {
  const s = String(raw || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s;
  // The Liquid emitter deliberately falls back to a bare CSS colour NAME when a
  // thread has no hex ("red", "yellow"), which is exactly what the 2D swatches
  // show. THREE.Color parses those, so accept them rather than dropping the
  // thread to the generic fallback and disagreeing with the swatch. Lowercased
  // so `webKey`/`threadKey` cache one material per colour either way.
  // `none` / `transparent` / `currentColor` are CSS keywords, not colours, and
  // THREE.Color would resolve them to white — they belong on the fallback.
  if (/^[a-z]{3,20}$/i.test(s) && !NON_COLOUR_KEYWORDS.has(s.toLowerCase())) {
    return s.toLowerCase();
  }
  return fallback;
};

/**
 * Material keys. Triangle bins (see triBins) are keyed by MATERIAL, not by
 * colour, so a mark that happens to be the same hex as the base shares one draw
 * group, and the thread / sleeve materials — which differ from the webbing in
 * roughness and opacity, not only in colour — stay distinct even on a webbing
 * whose base is the same hex as its thread. Both prefixes are four characters,
 * which is all matFor needs to know.
 */
const webKey = (raw, fallback) => `web:${String(hex(raw, fallback)).toLowerCase()}`;
const threadKey = (raw, fallback) => `thr:${String(hex(raw, fallback)).toLowerCase()}`;

/* ------------------------------------------- the cross-section diagram ---- */

/**
 * The Dimensions figure, read back out of the DOM.
 *
 * snippets/bc-webbing-cross-section.liquid draws the bar TO SCALE inside one
 * `<svg class="bc-webbing-cross-section__svg">`:
 *
 *   .__bar-outline            the capsule outline — x/y/width/height give both
 *                             the mm scale and the geometry (rx is height/2)
 *   g[clip-path] > rect 0,1   the top half and the bottom half, full width
 *   g[clip-path] > rect 2..5  the four rounded-edge quadrants, each exactly
 *                             half_h wide and sitting at the bar's left edge
 *                             or at (right edge - half_h) — which is precisely
 *                             the region the outline rounds off
 *   g[clip-path] > rect 6..   half-height marks: longitudinal stripes, x in mm
 *                             from the inner-left tangent point
 *   svg > circle              the optional inner edge circles (interior)
 *   svg > rect (not outline)  the optional inner pill (interior)
 *
 * WHICH SVG. A product with a `custom.diagram` cross_sections map renders one
 * pane per mapped colour value (snippets/bc-cross-section-variants.liquid),
 * all but the selected one carrying the `hidden` attribute — and
 * bc-variant-recolor.js swaps that attribute on variant change. So the first
 * svg in the document is the right one only by luck: the VISIBLE one is the
 * webbing the reader is looking at, and that is the one taken here.
 *
 * Everything is returned in millimetres, `b` measured across the width from the
 * centre, so the caller never sees a viewBox unit. If the figure is missing or
 * malformed we fall back to the sewing JSON's top-face projection, which is the
 * same data one half deep (bc-sewing-topface-json.liquid emits the TOP half
 * only — base, the two top edge caps, top-side marks).
 */
function readCrossSection(web) {
  const topFace = web.topFace || {};
  const scene = web.scene || {};
  const edges = topFace.edges || [];
  const baseTop = hex(topFace.base, '#7E2CD0');
  const baseBottom = hex(scene.colorLower, baseTop);
  const cs = {
    top: baseTop,
    bottom: baseBottom,
    edgeLT: hex(edges[0], baseTop),
    edgeRT: hex(edges[1], baseTop),
    edgeLB: baseBottom,
    edgeRB: baseBottom,
    marks: (topFace.marks || []).map((m) => ({
      x: Number(m.x) || 0, w: Number(m.w) || 0.5, side: 'top', color: hex(m.color, baseTop),
    })),
    pill: null,
    discs: [],
    source: 'sewing-json',
  };

  const panes = [...document.querySelectorAll('.bc-webbing-cross-section__svg')];
  // `hidden` is how the pane swap hides a pane, and how a collapsed wrapper
  // would hide the lot; the first pane nothing hides is the visible one.
  const svg = panes.find((s) => !s.closest('[hidden]')) || panes[0] || null;
  const outline = svg && svg.querySelector('.bc-webbing-cross-section__bar-outline');
  const num = (el, a) => parseFloat(el.getAttribute(a));
  if (!outline) return cs;
  const X = num(outline, 'x'); const Y = num(outline, 'y');
  const W = num(outline, 'width'); const H = num(outline, 'height');
  const wide = Number(web.widthMm) || 0;
  if (!(W > 0) || !(H > 0) || !(wide > 0)) return cs;

  const upm = W / wide;             // viewBox units per millimetre
  const halfH = H / 2;
  const innerL = X + halfH;         // the caps' inner tangent points
  const innerR = X + W - halfH;
  const mm = (px) => px / upm;
  const near = (a, b) => Math.abs(a - b) < 0.75;

  const g = svg.querySelector('g[clip-path]');
  const rects = g ? [...g.querySelectorAll('rect')] : [];
  if (rects.length < 2) return cs;
  const fill = (el, fb) => hex(el.getAttribute('fill'), fb);

  cs.source = 'cross-section';
  cs.top = fill(rects[0], baseTop);
  cs.bottom = fill(rects[1], cs.top);
  cs.edgeLT = cs.top; cs.edgeRT = cs.top;
  cs.edgeLB = cs.bottom; cs.edgeRB = cs.bottom;
  cs.marks = [];

  // Everything after the two base halves is either one of the four edge
  // quadrants (fixed signature: half_h wide, flush with a cap) or a mark.
  for (let i = 2; i < rects.length; i += 1) {
    const el = rects[i];
    const x = num(el, 'x'); const y = num(el, 'y'); const w = num(el, 'width');
    const bottomHalf = !near(y, Y);
    if (near(w, halfH) && (near(x, X) || near(x, innerR))) {
      const left = near(x, X);
      const c = fill(el, bottomHalf ? cs.bottom : cs.top);
      if (left && bottomHalf) cs.edgeLB = c;
      else if (left) cs.edgeLT = c;
      else if (bottomHalf) cs.edgeRB = c;
      else cs.edgeRT = c;
    } else {
      cs.marks.push({
        x: mm(x - innerL),
        w: mm(w),
        side: bottomHalf ? 'bottom' : 'top',
        color: fill(el, bottomHalf ? cs.bottom : cs.top),
      });
    }
  }

  // Interior detailing: direct children of the <svg>, outside the clip group.
  [...svg.children].forEach((el) => {
    const tag = (el.localName || el.tagName || '').toLowerCase();
    if (tag === 'circle') {
      cs.discs.push({
        b: mm(num(el, 'cx') - X) - wide / 2,
        r: mm(num(el, 'r')),
        color: fill(el, '#d9d9d9'),
      });
    } else if (tag === 'rect' && el !== outline) {
      cs.pill = {
        b0: mm(num(el, 'x') - X) - wide / 2,
        b1: mm(num(el, 'x') + num(el, 'width') - X) - wide / 2,
        halfThick: (num(el, 'height') / H) * (Number(web.thicknessMm) || 0) / 2,
        color: fill(el, '#d9d9d9'),
      };
    }
  });

  return cs;
}

/* ------------------------------------------------------- centrelines ------ */

/**
 * A cosine ease from (x0,y0) to (x1,y1), sampled at ~`step` mm and EXCLUDING
 * the first point so segments concatenate without duplicates. The join's lap
 * ramps — where a piece changes level smoothly rather than kinking.
 */
function ease(x0, y0, x1, y1, step) {
  const n = Math.max(2, Math.ceil(Math.abs(x1 - x0) / step));
  const out = [];
  for (let i = 1; i <= n; i += 1) {
    const t = i / n;
    out.push({ x: x0 + (x1 - x0) * t, y: y0 + (y1 - y0) * (1 - Math.cos(Math.PI * t)) / 2 });
  }
  return out;
}

/**
 * Sample a parametric mm-space curve `f(t)`, t in [0,1], at even steps of ARC
 * (not of parameter) no longer than `step`, EXCLUDING t = 0 so the result
 * concatenates onto a running polyline the way ease() does. Even spacing is
 * what keeps the swept quads square-ish through a curve whose speed varies by
 * half again between its ends and its middle.
 */
function sampleArc(f, step) {
  const N = 512;
  const raw = [];
  for (let i = 0; i <= N; i += 1) raw.push(f(i / N));
  const acc = [0];
  for (let i = 1; i <= N; i += 1) {
    acc.push(acc[i - 1] + Math.hypot(raw[i].x - raw[i - 1].x, raw[i].y - raw[i - 1].y));
  }
  const L = acc[N];
  const m = Math.max(2, Math.ceil(L / step));
  const out = [];
  let j = 0;
  for (let k = 1; k <= m; k += 1) {
    const target = (L * k) / m;
    while (j < N - 1 && acc[j + 1] < target) j += 1;
    const seg = acc[j + 1] - acc[j] || 1;
    const u = (target - acc[j]) / seg;
    out.push({
      x: raw[j].x + (raw[j + 1].x - raw[j].x) * u,
      y: raw[j].y + (raw[j + 1].y - raw[j].y) * u,
    });
  }
  return out;
}

/* ------------------------------------------------------- the teardrop ----- */

/**
 * THE EYE'S SILHOUETTE, ported from assets/bc-segment-diagram.js.
 *
 * Its `hairpinPath(throatX, dir, foldX, startY, topY, botY, endY)` draws the
 * side view's loop as, per strand:
 *
 *   C  X(0.45) startY   X(0.55) topY   X(1) topY      <- throat to belly
 *   A  capR capR 0 0 s  X(1) botY                     <- the fold cap
 *
 * i.e. ONE cubic whose two y-controls sit ON the end levels — horizontal
 * tangent where it leaves the sewn patch AND horizontal tangent where it meets
 * the cap — with its x-controls at 0.45 / 0.55 of the span; then a semicircle
 * whose radius `capR = (botY - topY) / 2` IS the eye half-height. There is no
 * straight run in it, which is exactly what makes the outline a teardrop: the
 * strands are pinched together at the throat, bow apart with steadily
 * increasing then decreasing curvature, and are widest at the cap's tangent
 * point, close to the nose.
 *
 * Here `startY` is the layer's own centreline offset (thickness / 2, the same
 * split-halves value the 2D uses), `topY` is B, and the cap is the U-turn.
 */
const EYE_CX1 = 0.45;                        // hairpinPath's two x-controls,
const EYE_CX2 = 0.55;                        // as fractions of the span
// Span (throat -> belly) as a multiple of the eye half-height. Unlike the flat
// 2D figure — where the eye's drawn x-extent IS the flat opening, so the
// proportion falls out of the layout — an OPENED eye has to be given one. 3.6
// solves (see loopCentreline) to a 26 mm half-height on a 139 mm opening.
// Raise it for a longer, narrower eye; lower it for a rounder one.
const EYE_ASPECT = 3.6;

/** The throat->belly cubic, in mm, as a function of its Bezier parameter. */
function eyeCubic(span, B, y0) {
  return (t) => {
    const m = 1 - t;
    return {
      x: 3 * m * m * t * (EYE_CX1 * span) + 3 * m * t * t * (EYE_CX2 * span) + t * t * t * span,
      y: y0 * (m * m * m + 3 * m * m * t) + B * (3 * m * t * t + t * t * t),
    };
  };
}

/**
 * Arc length of ONE strand through the eye at half-height B: the cubic, plus
 * half the U-turn (the other half belongs to the other strand). Monotonic in
 * B, which is what lets loopCentreline bisect it.
 */
function eyeStrandLen(B, y0) {
  const f = eyeCubic(EYE_ASPECT * B, B, y0);
  let L = 0;
  let p = f(0);
  for (let i = 1; i <= 512; i += 1) {
    const q = f(i / 512);
    L += Math.hypot(q.x - p.x, q.y - p.y);
    p = q;
  }
  return L + (Math.PI * B) / 2;
}

/**
 * The SEWN LOOP, as one continuous strand.
 *
 * The model is assets/bc-segment-diagram.js's `endGeometry`, opened out of the
 * flat hairpin into the eye you would actually put a carabiner through:
 *
 *   eye (fold tip -> throat)  = the OPENING, of FLAT webbing per strand
 *   two-layer sewn zone       = pattern length + a 5 mm tail tab
 *   fold (tip -> tail cut end)= opening + pattern length + 5
 *
 * Opening the eye keeps that first line honest. The 2D figure can draw the eye
 * at its flat length because it draws it flat; here the eye is a real teardrop,
 * so its ARC has to be solved: the whole silhouette is one shape scaled by its
 * half-height B, `eyeStrandLen` is monotonic in B, and B is bisected until each
 * strand measures the opening to well under a micron. "Opening 139 mm"
 * therefore measures webbing, not the gap it happens to make.
 *
 * ORDER MATTERS. The polyline starts at the far end of the standing part and
 * finishes at the tail's cut edge, which puts the strand's +normal side (the
 * `topFace`) on the OUTSIDE all the way round: top face up on the standing
 * part, top face down on the tail underneath it, the underside only ever facing
 * into the eye. That is "top face out" — the Segment Builder's default loop —
 * arrived at by construction rather than painted on, and it is why the needle
 * thread lands on the top face.
 */
function loopCentreline(g) {
  const half = g.thicknessMm / 2;            // the two layers' centreline offset
  const openingMm = g.openingMm;
  const tailMm = g.foldMm - g.openingMm;     // = pattern length + 5 mm tab
  // How much standing part we show. It has to OUTRUN both the tail lying under
  // it and the patch stitched through them, or the stitching floats past the
  // end of the drawn webbing (pattern 504/514: 160 mm patch, 165 mm tail). The
  // 122 floor keeps the familiar 502/512 framing, where both terms are shorter.
  const standMm = Math.max(122, tailMm + 24, g.patternLengthMm + 24);
  const step = 1.5;

  // Solve the eye half-height so one strand's arc IS the opening. Bracket:
  // the strand is at least the quarter-turn (pi*B/2) and at most that plus the
  // cubic's span, so B < opening comfortably; 60 halvings takes the residual
  // to ~1e-16 mm and costs nothing.
  let lo = 0.5;
  let hi = Math.max(2, openingMm);
  for (let i = 0; i < 60; i += 1) {
    const mid = (lo + hi) / 2;
    if (eyeStrandLen(mid, half) < openingMm) lo = mid; else hi = mid;
  }
  const B = (lo + hi) / 2;                   // eye half-height, opened
  const span = EYE_ASPECT * B;               // throat -> belly, = the cap's centre x

  const pts = [{ x: -standMm, y: half }, { x: 0, y: half }];
  // throat -> belly, the upper strand
  pts.push(...sampleArc(eyeCubic(span, B, half), step));
  // the fold cap: a semicircle of radius B about (span, 0), the `A capR capR`
  // arc, tangent to the cubic's flat arrival at both of its ends.
  // EVEN, always: the fold tip has to BE a sample, because noseIndex is what
  // the sleeve is centred on and what the arc-length check measures to. (An
  // odd count puts "the middle sample" half a step past the tip — half a
  // millimetre of phantom opening, and a sleeve half a millimetre off-centre.)
  const arcN = 2 * Math.max(7, Math.ceil((Math.PI * B) / (2 * step)));
  let noseIndex = 0;
  for (let i = 1; i <= arcN; i += 1) {
    const th = Math.PI / 2 - (Math.PI * i) / arcN;
    pts.push({ x: span + B * Math.cos(th), y: B * Math.sin(th) });
    if (i === Math.round(arcN / 2)) noseIndex = pts.length - 1;
  }
  // belly -> throat, the lower strand: the same cubic mirrored and reversed.
  // slice(1) drops its (span, -B), which the cap above already ended on — a
  // duplicated sample is a zero-length segment and a degenerate quad.
  const down = sampleArc(eyeCubic(span, B, half), step)
    .map((p) => ({ x: p.x, y: -p.y })).reverse();
  pts.push(...down.slice(1), { x: 0, y: -half });
  pts.push({ x: -tailMm, y: -half });

  return {
    pts,
    noseIndex,
    noseTipX: span + B,
    eyeHalfHeight: B,
    tailEndX: -tailMm,
    // Sewn patch: starts AT the throat (x = 0) and grows toward the tail, on
    // the standing part's top surface. The 5 mm tab is what is left over.
    patch: { x0: -g.patternLengthMm, x1: 0, top: half * 2, bottom: -half * 2 },
  };
}

/**
 * The JOIN STITCH, as two lap-jointed pieces.
 *
 * drawJoinSideDocs's model: each piece runs in on the line, S-ramps half a
 * thickness onto its overlap level and runs to its own cut end, so the stacked
 * (bumped) region IS the overlap — pattern length + 10, a 5 mm tab at each end,
 * which puts the stitched patch centred in it.
 */
function joinCentrelines(g) {
  const half = g.thicknessMm / 2;
  const o = g.overlapMm / 2;
  const ramp = 16;
  const run = 88;
  const under = [{ x: -o - ramp - run, y: 0 }, { x: -o - ramp, y: 0 }]
    .concat(ease(-o - ramp, 0, -o, -half, 1.5))
    .concat([{ x: o, y: -half }]);
  const over = [{ x: -o, y: half }, { x: o, y: half }]
    .concat(ease(o, half, o + ramp, 0, 1.5))
    .concat([{ x: o + ramp + run, y: 0 }]);
  return {
    under,
    over,
    o,
    patch: {
      x0: -g.patternLengthMm / 2,
      x1: g.patternLengthMm / 2,
      top: half * 2,
      bottom: -half * 2,
    },
  };
}

/* ------------------------------------------------------------- meshes ----- */

const cross3 = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const unit3 = (v) => {
  const L = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / L, v[1] / L, v[2] / L];
};

/**
 * Frames for a centreline in the XY plane: n is the left normal (the strand's
 * `topFace` side), b is +Z (across the webbing's width). The convention the
 * mesh builder assumes throughout is T = n x b.
 */
function xyFrames(pts) {
  return pts.map((p, i) => {
    const a = pts[Math.max(0, i - 1)];
    const c = pts[Math.min(pts.length - 1, i + 1)];
    let tx = c.x - a.x;
    let ty = c.y - a.y;
    const L = Math.hypot(tx, ty) || 1;
    tx /= L; ty /= L;
    return { p: [p.x, p.y, 0], n: [-ty, tx, 0], b: [0, 0, 1] };
  });
}

/**
 * Triangles bucketed by material key. One non-indexed BufferGeometry comes out,
 * with one group per key in insertion order, so the mesh's material array is
 * just the key list mapped through the registry — a webbing with a stripe down
 * it is still one mesh and one draw call per distinct material, and a colour
 * can never be assigned to the wrong part of the section.
 */
function triBins() {
  const bins = new Map();
  let flag = 0;                      // 1 while a DECAL is being drawn
  const grab = (k) => {
    let e = bins.get(k);
    if (!e) { e = { pos: [], nor: [], dec: [] }; bins.set(k, e); }
    return e;
  };
  return {
    // The interior features on a cut face (the cross-section's pill and its
    // edge circles) are single-sided patches lifted proud of the face they sit
    // on — decoration ON the solid, not part of it. Flagging them costs one
    // byte a triangle and is what lets the manifold check tell an open decal
    // from a hole in the model.
    decal(on) { flag = on ? 1 : 0; },
    tri(k, a, na, b, nb, c, nc) {
      const e = grab(k);
      e.pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
      e.nor.push(na[0], na[1], na[2], nb[0], nb[1], nb[2], nc[0], nc[1], nc[2]);
      e.dec.push(flag);
    },
    build(THREE) {
      const keys = [...bins.keys()];
      let n = 0;
      let t = 0;
      keys.forEach((k) => { n += bins.get(k).pos.length; t += bins.get(k).dec.length; });
      const pos = new Float32Array(n);
      const nor = new Float32Array(n);
      const dec = new Uint8Array(t);
      const geo = new THREE.BufferGeometry();
      let o = 0;
      let d = 0;
      keys.forEach((k, i) => {
        const e = bins.get(k);
        pos.set(e.pos, o);
        nor.set(e.nor, o);
        dec.set(e.dec, d);
        geo.addGroup(o / 3, e.pos.length / 3, i);
        o += e.pos.length;
        d += e.dec.length;
      });
      geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      geo.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
      return { geo, keys, dec };
    },
  };
}

/**
 * Stadium geometry, in the frame's (b, n) plane: b across the width, n through
 * the thickness. The round is a true semicircle of radius thickness/2 at each
 * selvage — exactly the `rx = bar_h / 2` the cross-section SVG draws — leaving
 * a flat of `fh` half-width in the middle. (A section thicker than it is wide
 * would have no flat at all; clamping r keeps that degenerate case a closed
 * capsule rather than a self-intersecting one.)
 */
function ringDims(width, thick) {
  const hw = width / 2;
  const ht = thick / 2;
  const r = Math.min(ht, hw);
  return { hw, ht, r, fh: hw - r };
}

/**
 * A colour design for a section, in the coordinates ringDims hands out:
 *
 *   top / bottom   ordered, gap-free colour spans across the FLAT region,
 *                  each {from, to, key} with from/to in mm from the centre —
 *                  these become longitudinal stripes on the strand
 *   edgeLT/LB/RT/RB the four quadrants of the two rounds
 *   pill / discs   interior features: drawn on cut faces only
 */
function plainDesign(key, dims) {
  return {
    top: [{ from: -dims.fh, to: dims.fh, key }],
    bottom: [{ from: -dims.fh, to: dims.fh, key }],
    edgeLT: key, edgeLB: key, edgeRT: key, edgeRB: key,
    pill: null, discs: [],
  };
}

/** The cross-section's marks, cut into the base span of one face. */
function markSpans(baseHex, marks, side, fh) {
  const baseKey = webKey(baseHex, '#2b2b2b');
  let spans = [{ from: -fh, to: fh, key: baseKey }];
  marks.filter((m) => m.side === side).forEach((m) => {
    const a = Math.max(-fh, Math.min(fh, -fh + m.x));
    const b = Math.max(a, Math.min(fh, a + m.w));
    if (!(b - a > 1e-6)) return;
    const next = [];
    spans.forEach((s) => {
      if (s.to <= a || s.from >= b) { next.push(s); return; }
      if (s.from < a - 1e-9) next.push({ from: s.from, to: a, key: s.key });
      if (s.to > b + 1e-9) next.push({ from: b, to: s.to, key: s.key });
    });
    next.push({ from: a, to: b, key: webKey(m.color, baseHex) });
    next.sort((p, q) => p.from - q.from);
    spans = next;
  });
  return spans;
}

function webbingDesign(cs, dims) {
  return {
    top: markSpans(cs.top, cs.marks, 'top', dims.fh),
    bottom: markSpans(cs.bottom, cs.marks, 'bottom', dims.fh),
    edgeLT: webKey(cs.edgeLT, cs.top),
    edgeLB: webKey(cs.edgeLB, cs.bottom),
    edgeRT: webKey(cs.edgeRT, cs.top),
    edgeRB: webKey(cs.edgeRB, cs.bottom),
    pill: cs.pill && { ...cs.pill, key: webKey(cs.pill.color, cs.top) },
    discs: (cs.discs || []).map((d) => ({ ...d, key: webKey(d.color, cs.top) })),
  };
}

/**
 * The closed profile ring, walked so that the outward normal is the travel
 * direction turned CLOCKWISE in the (b, n) plot — the winding the sweep below
 * assumes. Each vertex carries the material key of the segment that STARTS at
 * it, plus `live`, which drops the zero-length segments a zero-width mark or a
 * flat-less section would otherwise leave behind.
 *
 * Normals are the exact analytic ones and are SHARED between neighbouring
 * segments, so every round shades as one continuous curve. There is no crease
 * to decide about at the flat/round junction: a stadium is tangent-continuous
 * there, and both sides genuinely have normal +-n. What marks the selvage is
 * the colour change, which is where the diagram puts it.
 */
function stadiumRing(dims, seg, design) {
  const { ht, r, fh } = dims;
  const ring = [];
  const push = (b, n, ub, un, key) => {
    const L = Math.hypot(ub, un) || 1;
    ring.push({ b, n, ub: ub / L, un: un / L, key });
  };
  const N = Math.max(2, seg) * 2;                 // segments per half-round
  const half = (cb, from, keyA, keyB) => {
    for (let i = 1; i < N; i += 1) {
      const th = from + (Math.PI * i) / N;
      push(cb + r * Math.cos(th), r * Math.sin(th), Math.cos(th), Math.sin(th),
        i < N / 2 ? keyA : keyB);
    }
  };

  // top flat, travelling +fh -> -fh (so the span list is walked backwards)
  for (let i = design.top.length - 1; i >= 0; i -= 1) {
    push(design.top[i].to, ht, 0, 1, design.top[i].key);
  }
  push(-fh, ht, 0, 1, design.edgeLT);             // = the left round at 90 deg
  half(-fh, Math.PI / 2, design.edgeLT, design.edgeLB);
  push(-fh, -ht, 0, -1, design.bottom[0].key);    // = the left round at 270 deg
  // bottom flat, travelling -fh -> +fh
  for (let i = 1; i < design.bottom.length; i += 1) {
    push(design.bottom[i].from, -ht, 0, -1, design.bottom[i].key);
  }
  push(fh, -ht, 0, -1, design.edgeRB);            // = the right round at 270 deg
  half(fh, Math.PI * 1.5, design.edgeRB, design.edgeRT);
  // the right round's 90 deg vertex IS ring[0] — the loop closes on it.

  ring.forEach((v, i) => {
    const w = ring[(i + 1) % ring.length];
    v.live = Math.hypot(w.b - v.b, w.n - v.n) > 1e-9;
  });
  return ring;
}

function makeSection(width, thick, seg, design) {
  const dims = ringDims(width, thick);
  const d = design || plainDesign('web:#2b2b2b', dims);
  return { dims, seg: Math.max(2, seg), design: d, ring: stadiumRing(dims, Math.max(2, seg), d) };
}

/**
 * One profile ring placed in the world on a frame. `inset` shrinks it along its
 * own normals (a stadium offset inward is still a stadium), `push` slides it
 * along the tangent, and cosP/sinP tilt the normals toward that tangent — which
 * between them is all a rounded cut end needs.
 */
function ringWorld(f, ring, inset, push, cosP, sinP) {
  const T = cross3(f.n, f.b);
  return ring.map((v) => {
    const b = v.b - inset * v.ub;
    const n = v.n - inset * v.un;
    return {
      p: [0, 1, 2].map((k) => f.p[k] + f.n[k] * n + f.b[k] * b + T[k] * push),
      nrm: unit3([0, 1, 2].map((k) => (f.n[k] * v.un + f.b[k] * v.ub) * cosP + T[k] * sinP)),
      key: v.key,
      live: v.live,
    };
  });
}

/** Stitch consecutive rings into quads. `rings` must advance along +T. */
function sweepRings(sink, rings) {
  for (let i = 0; i < rings.length - 1; i += 1) {
    const R = rings[i]; const S = rings[i + 1];
    for (let j = 0; j < R.length; j += 1) {
      if (!R[j].live) continue;
      const k = (j + 1) % R.length;
      sink.tri(R[j].key, R[j].p, R[j].nrm, S[k].p, S[k].nrm, R[k].p, R[k].nrm);
      sink.tri(R[j].key, R[j].p, R[j].nrm, S[j].p, S[j].nrm, S[k].p, S[k].nrm);
    }
  }
}

/**
 * The cut face itself: the cross-section design, drawn as triangles in the
 * frame's (b, n) plane. Its parts are the SVG's parts — the two base halves
 * over the flat, a quadrant fan for each half of each round, then the interior
 * pill and circles a hair proud so they cannot z-fight the halves they sit on.
 * A cut end of this webbing is therefore a small copy of the Dimensions figure,
 * which is the only honest thing for it to be.
 */
function capFace(sink, f, section, tSign, push, inset) {
  const { dims, design, seg } = section;
  const T = cross3(f.n, f.b);
  const nrm = [T[0] * tSign, T[1] * tSign, T[2] * tSign];
  const LIFT = 0.06;
  const at = (b, n, lift) => [0, 1, 2].map(
    (k) => f.p[k] + f.n[k] * n + f.b[k] * b + T[k] * (push + tSign * (lift || 0))
  );
  // Vertices are given counter-clockwise in the (b, n) plot, which faces -T
  // (b x n = -T, since n x b = T); the +T faces are the same triangles flipped.
  const t3 = (key, A, B, C, lift) => {
    const a = at(A[0], A[1], lift); const b = at(B[0], B[1], lift); const c = at(C[0], C[1], lift);
    if (tSign > 0) sink.tri(key, a, nrm, c, nrm, b, nrm);
    else sink.tri(key, a, nrm, b, nrm, c, nrm);
  };
  const rect = (key, b0, b1, n0, n1, lift) => {
    if (!(b1 - b0 > 1e-9) || !(n1 - n0 > 1e-9)) return;
    t3(key, [b0, n0], [b1, n0], [b1, n1], lift);
    t3(key, [b0, n0], [b1, n1], [b0, n1], lift);
  };
  const fan = (key, cb, rad, a0, a1, lift) => {
    if (!(rad > 1e-9)) return;
    const n = Math.max(2, Math.round((seg * Math.abs(a1 - a0)) / (Math.PI / 2)));
    for (let i = 0; i < n; i += 1) {
      const t0 = a0 + ((a1 - a0) * i) / n;
      const t1 = a0 + ((a1 - a0) * (i + 1)) / n;
      t3(key, [cb, 0], [cb + rad * Math.cos(t0), rad * Math.sin(t0)],
        [cb + rad * Math.cos(t1), rad * Math.sin(t1)], lift);
    }
  };

  const ht = dims.ht - inset;
  const rad = Math.max(0, dims.r - inset);
  const fh = dims.fh;
  design.top.forEach((s) => rect(s.key, s.from, s.to, 0, ht));
  design.bottom.forEach((s) => rect(s.key, s.from, s.to, -ht, 0));
  fan(design.edgeLT, -fh, rad, Math.PI / 2, Math.PI);
  fan(design.edgeLB, -fh, rad, Math.PI, Math.PI * 1.5);
  fan(design.edgeRB, fh, rad, Math.PI * 1.5, Math.PI * 2);
  fan(design.edgeRT, fh, rad, 0, Math.PI / 2);

  // Everything from here on is lifted proud of the face: decoration on the
  // solid rather than part of its skin, and flagged as such.
  sink.decal(true);
  design.discs.forEach((d) => {
    const r = Math.min(d.r, rad * 0.92);
    if (r > 1e-9) fan(d.key, d.b, r, 0, Math.PI * 2, LIFT);
  });
  const pill = design.pill;
  if (pill && ht > 0) {
    const rr = Math.min(pill.halfThick, ht * 0.98);
    const b0 = Math.max(-fh, pill.b0) + rr;
    const b1 = Math.min(fh, pill.b1) - rr;
    if (b1 > b0) {
      rect(pill.key, b0, b1, -rr, rr, LIFT);
      fan(pill.key, b0, rr, Math.PI / 2, Math.PI * 1.5, LIFT);
      fan(pill.key, b1, rr, -Math.PI / 2, Math.PI / 2, LIFT);
    }
  }
  sink.decal(false);
}

/**
 * ONE END OF A SWEPT SECTION, in either of the two treatments a drawing
 * distinguishes. `cr` is the round-over radius, and it is the whole difference:
 *
 *   cr > 0  A ROUNDED (cut) end. The profile eases inward by cr while advancing
 *           cr along the tangent — a quarter-round about the entire perimeter,
 *           K rings of it — and the remaining face, a stadium inset by cr,
 *           closes it. Nothing meets at a right angle. (The round grows OUT of
 *           the last frame rather than being cut back into it, so the strand is
 *           cr longer than the polyline says: 1.25 mm on a 210 mm fold, six
 *           tenths of one percent, and a bullnose rather than a lie.)
 *   cr = 0  A SQUARE (free) end: the section's own ring, closed by the full
 *           cross-section face, at exactly the polyline's last point. The
 *           drafting convention for "this carries on beyond the drawing" — and
 *           the treatment the stitch tubes have always used.
 *
 * Either way capFace draws the face, so the little copy of the Dimensions
 * cross-section is on BOTH kinds of end; a round-over only squashes it, by
 * exactly the material the round takes off. `cr` is therefore clamped to 75% of
 * the half-thickness: at 100% the face has no height left at all, and a cut end
 * of webbing that shows no cross-section is a worse drawing than a slightly
 * tighter bullnose. Ring count follows the radius (~0.16 mm of arc per segment)
 * so a 1.25 mm round shades as smoothly as a 0.5 mm one.
 */
function endCap(sink, f, section, tSign, radius) {
  const cr = Math.min(radius, section.dims.ht * 0.75, section.dims.hw * 0.9);
  if (!(cr > 0)) { capFace(sink, f, section, tSign, 0, 0); return; }
  const K = Math.max(3, Math.round(cr * 6));
  const rings = [];
  for (let k = 0; k <= K; k += 1) {
    const phi = ((k / K) * Math.PI) / 2;
    rings.push(ringWorld(f, section.ring, cr * (1 - Math.cos(phi)),
      tSign * cr * Math.sin(phi), Math.cos(phi), tSign * Math.sin(phi)));
  }
  // The near end's fillet marches along -T, so its ring list is reversed to
  // keep the sweep's "S is further along +T" contract (and its winding).
  sweepRings(sink, tSign > 0 ? rings : rings.slice().reverse());
  capFace(sink, f, section, tSign, tSign * cr, cr);
}

/**
 * Sweep one section along one frame list, ending each end in its own
 * treatment. `crStart` is frames[0]'s (the -T end) and `crEnd` frames[last]'s;
 * one argument means both ends alike, which is what the sleeve and the stitch
 * tubes want.
 */
function sweepInto(sink, frames, section, crStart, crEnd) {
  sweepRings(sink, frames.map((f) => ringWorld(f, section.ring, 0, 0, 1, 0)));
  endCap(sink, frames[0], section, -1, crStart);
  endCap(sink, frames[frames.length - 1], section, 1,
    crEnd === undefined ? crStart : crEnd);
}

/** Every mesh this module builds, kept for the manifold check (see the foot). */
const PARTS = [];

function meshOf(THREE, sink, matFor, extra) {
  const { geo, keys, dec } = sink.build(THREE);
  const mesh = new THREE.Mesh(geo, keys.map(matFor));
  Object.assign(mesh, extra || {});
  PARTS.push({ name: mesh.name || 'part', geo, dec });
  return mesh;
}

/* ------------------------------------------------------------ stitching --- */

/** "M9 0L4.5 0L0 0…" -> subpath point lists, in the machine file's mm space. */
function parsePaths(list) {
  const out = [];
  (list || []).forEach((d) => {
    let cur = null;
    const re = /([ML])\s*(-?[\d.]+)[\s,]+(-?[\d.]+)/g;
    let m = re.exec(d);
    while (m) {
      const pt = { x: parseFloat(m[2]), y: parseFloat(m[3]) };
      if (m[1] === 'M' || !cur) { cur = [pt]; out.push(cur); } else cur.push(pt);
      m = re.exec(d);
    }
  });
  return out.filter((s) => s.length > 1);
}

/**
 * The machine's sew runs, as real thread on the real surface.
 *
 * `pattern_paths` is the PLK-G file's stitch polyline in mm (written by
 * docs/metafield-audit/migration/upload_sewing_pattern_images.py --sync-paths);
 * its points are the machine's stitches, so every segment drawn here is a
 * stitch that machine actually lays. Placed exactly as drawStitchZone places it
 * in 2D: the art is fitted uniformly into the patternLength x patternWidth box.
 *
 * Each segment is extruded as its own little rounded tube rather than as one
 * continuous swept one: the runs reverse through ~170 degrees at every zigzag
 * edge, and a shared frame at a corner that sharp pinches into a bowtie. At
 * 0.55 mm across, the hairline gaps this leaves at the corners are far below
 * anything the zoom reaches. The profile is the same stadium the webbing uses,
 * which at 0.55 x 0.5 mm is all but round — thread, not extruded square bar.
 */
function stitchInto(sink, subpaths, place, section) {
  const seg = (p0, p1) => {
    const t = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]];
    const L = Math.hypot(t[0], t[1], t[2]);
    if (!(L > 1e-6)) return;
    const T = t.map((v) => v / L);
    const n = place.n;
    // b = T x n  (so that n x b = T, the builder's convention)
    sweepInto(sink, [{ p: p0, n, b: cross3(T, n) }, { p: p1, n, b: cross3(T, n) }], section, 0);
  };
  subpaths.forEach((sp) => {
    for (let i = 0; i < sp.length - 1; i += 1) seg(place.at(sp[i]), place.at(sp[i + 1]));
  });
}

/**
 * Where a pattern's art lands on a surface: the uniform "meet" fit of the art
 * box into the patternLength x patternWidth patch, centred across the webbing,
 * lifted `lift` mm proud of the face so it cannot z-fight with it.
 */
function patchPlacer(pattern, x0, y, lift, up, mirror) {
  const art = pattern.paths || {};
  const aw = Number(art.w) || pattern.patternLengthMm || 1;
  const ah = Number(art.h) || pattern.patternWidthMm || 1;
  const bw = pattern.patternLengthMm || aw;
  const bh = pattern.patternWidthMm || ah;
  const s = Math.min(bw / aw, bh / ah);
  const ox = (bw - aw * s) / 2;
  const oz = (ah * s) / 2;
  return {
    n: up > 0 ? [0, 1, 0] : [0, -1, 0],
    at: (p) => [
      x0 + ox + (mirror ? aw - p.x : p.x) * s,
      y + up * lift,
      p.y * s - oz,
    ],
  };
}

/* --------------------------------------------------------- the key block -- */

/**
 * The key's reading order, which is NOT the order the rows happen to be built
 * in. A drawing's key is read feature-first: the loop comes out Opening, Fold,
 * Sleeve, Pattern (the eye, the turn round it, what is slid over it, what holds
 * it) and the join Overlap, Pattern (the lap, then the stitching that makes
 * it).
 */
const LEGEND_ORDER = ['opening', 'fold', 'sleeve', 'overlap', 'pattern'];

/** One key row. `key` only orders the row (LEGEND_ORDER); it names nothing. */
const dimRow = (key, name, value) => ({ key, name, value });

/* --------------------------------------------------------------- models --- */

// THE TWO END TREATMENTS. A CUT end — a real end of a real piece of webbing —
// gets a generous round-over: 1.25 mm is 71% of a 1.75 mm half-thickness, a
// bullnose that leaves 1 mm of cut face for the mini cross-section to sit on. A
// FREE end is the drawing running out rather than the webbing, and gets the
// drafting convention for that: dead square.
const CUT_ROUND = 1.25;
const FREE_SQUARE = 0;
// Profile resolution: 8 segments per quarter round puts ~0.14 mm between
// samples on a 1.75 mm radius, which is smooth at any zoom the viewer allows.
const WEB_SEG = 8;
const THREAD_SEG = 2;
// Thread, in millimetres. Its own stadium section, all but round at this size.
const THREAD_W = 0.55;
const THREAD_T = 0.5;

/**
 * The pattern's DRAWN size: the art box `pattern_paths` carries (`w` x `h` in
 * the machine file's millimetres), which is what patchPlacer fits onto the
 * webbing and what the written rows quote — the catalog patternLength x
 * patternWidth is the fallback for a pattern with no paths. Trailing zeros are
 * trimmed, so 65.5 stays 65.5 and 20.0 reads 20.
 */
function patternDims(pat) {
  const art = pat.paths || {};
  const w = Number(art.w) > 0 ? Number(art.w) : Number(pat.patternLengthMm);
  const h = Number(art.h) > 0 ? Number(art.h) : Number(pat.patternWidthMm);
  if (!(w > 0) || !(h > 0)) return '';
  const n = (v) => String(Math.round(v * 10) / 10);
  return `${n(w)} × ${n(h)} mm`;
}

/**
 * "Amann Xtreme Tech with Dyneema 220-tex Red" -> "220-tex Red".
 *
 * The block is an ID plate, not a spec sheet: the brand and the fibre are the
 * same on every pattern a webbing is sewn with (the written rows say so in
 * full), and what tells the two threads apart in the picture is the count and
 * the colour. Anything that does not parse is passed through and let the CSS
 * ellipsis it, which is the only failure mode that cannot mislead.
 */
function shortThread(name) {
  const s = String(name || '').trim();
  const m = /([\d.]+\s*-?\s*tex)\s+(.+)$/i.exec(s);
  return m ? `${m[1].replace(/\s+/g, '')} ${m[2]}` : s;
}

/**
 * The panel's title block, as data. Deliberately terse and deliberately a
 * SUBSET: code, name, the stitch count and the two threads — the things you
 * would want stamped in the corner of the drawing you are looking at.
 * Everything else about the pattern, and every unit conversion, is in the
 * written rows the section renders below the figure.
 */
function patternPlate(pat, kindLabel) {
  const rows = [{ k: 'Stitches', v: pat.stitchCount ? String(pat.stitchCount) : '—' }];
  if (pat.needleThread) {
    rows.push({
      k: 'Needle', v: shortThread(pat.needleThread), sw: hex(pat.needleThreadColor, '#DC2C3E'),
    });
  }
  if (pat.bobbinThread) {
    rows.push({
      k: 'Bobbin', v: shortThread(pat.bobbinThread), sw: hex(pat.bobbinThreadColor, '#FFD60A'),
    });
  }
  return {
    code: String(pat.code || ''),
    kind: kindLabel,
    name: String(pat.name || ''),
    rows,
  };
}

/**
 * The sewn-zone dimensions a build needs, with the catalog equations as the
 * fallbacks — the same two the sewing pipeline writes into the metaobjects
 * (see CLAUDE.md's sewing-pattern section):
 *
 *   fold    = opening + pattern length + 5     (the 5 mm tail tab)
 *   overlap = pattern length + 10              (a 5 mm tab at each end)
 *
 * A pattern that carries the numbers uses them; one that carries only its
 * opening still draws, and one that carries neither does not draw at all (the
 * host is flagged failed and keeps its flat art).
 */
function patternGeometry(pat, kind) {
  const art = pat.paths || {};
  const patternLengthMm = mmOf(pat.patternLengthMm) || mmOf(art.w);
  if (kind === 'join') {
    const overlapMm = mmOf(pat.overlapMm) || (patternLengthMm ? patternLengthMm + 10 : 0);
    if (!(overlapMm > 0)) return null;
    return { patternLengthMm, overlapMm };
  }
  const openingMm = mmOf(pat.openingMm) || mmText(pat.openingText);
  if (!(openingMm > 0)) return null;
  let foldMm = mmOf(pat.foldMm) || mmText(pat.foldText);
  if (!(foldMm > openingMm)) foldMm = openingMm + patternLengthMm + 5;
  if (!(foldMm > openingMm)) return null;
  return { patternLengthMm, openingMm, foldMm };
}

function buildLoop(THREE, cfg, kit, pat) {
  const web = cfg.web;
  const dims = patternGeometry(pat, 'loop');
  if (!dims) return null;
  const g = {
    thicknessMm: web.thicknessMm,
    openingMm: dims.openingMm,
    foldMm: dims.foldMm,
    patternLengthMm: dims.patternLengthMm,
  };
  const geom = loopCentreline(g);
  const frames = xyFrames(geom.pts);
  const group = new THREE.Group();

  const body = triBins();
  // frames[0] is the far end of the STANDING part — the webbing carrying on to
  // the rest of the line, so square — and frames[last] is the TAIL's cut edge,
  // 5 mm past the patch, which is rounded over. loopCentreline's point order is
  // what makes that assignment; it is the same order that puts the top face out.
  sweepInto(body, frames, kit.web, FREE_SQUARE, CUT_ROUND);
  group.add(meshOf(THREE, body, kit.matFor, { name: 'loop-webbing' }));

  // The sleeve is slid on BEFORE the fold, so a 200 mm sleeve is 100 mm of it
  // along each strand from the nose — the same "S/2 along the eye" model
  // sleeveCoverage draws in 2D, here simply as the span of centreline within
  // 100 mm of the fold tip. It is drawn only when the webbing's end-treatment
  // vocabulary offers a sleeved end (`sleeved` in the config, the same flag the
  // 2D figure reads), and a shade thicker than the webbing, because that is
  // what a tube is.
  const sleeveMm = mmOf(web.sleeveDefaultMm) || 200;
  const arc = [0];
  for (let i = 1; i < geom.pts.length; i += 1) {
    arc.push(arc[i - 1]
      + Math.hypot(geom.pts[i].x - geom.pts[i - 1].x, geom.pts[i].y - geom.pts[i - 1].y));
  }
  const aNose = arc[geom.noseIndex];

  // The check the teardrop solve exists for, measured on the BUILT polyline
  // rather than on the theory: pts[1] is the throat, geom.noseIndex the fold
  // tip, and the webbing between them is what "Opening" names. The residual is
  // the chord-vs-arc deficit of a 1.5 mm sampling (a few microns on a
  // semicircle of this radius), not slop in the solve. Recorded on the debug
  // handle rather than logged — a documentation page owes the console nothing.
  const eyeArcMm = arc[geom.noseIndex] - arc[1];
  debug.loops.push({
    code: String(pat.code || pat.handle || ''),
    openingMm: dims.openingMm,
    eyeArcMm,
    deltaMm: eyeArcMm - dims.openingMm,
    eyeHalfHeightMm: geom.eyeHalfHeight,
    spanMm: geom.noseTipX - geom.eyeHalfHeight,
  });

  // The covered span, by arc length either side of the nose: the frames the
  // sleeve tube is swept along, and (below) whether there is a sleeve at all.
  let s0 = -1;
  let s1 = -1;
  for (let i = 0; i < frames.length; i += 1) {
    if (Math.abs(arc[i] - aNose) <= sleeveMm / 2) { if (s0 < 0) s0 = i; s1 = i; }
  }
  const span = s0 < 0 ? [] : frames.slice(s0, s1 + 1);
  const sleeved = web.sleeved !== false && span.length > 3;
  let sleeveMesh = null;
  if (sleeved) {
    const sl = triBins();
    // A sleeve is a length of tube cut off a roll: BOTH of its ends are cut
    // ends, and neither of them is the drawing running out.
    sweepInto(sl, span, kit.sleeve, CUT_ROUND);
    sleeveMesh = meshOf(THREE, sl, kit.matFor, { name: 'loop-sleeve', renderOrder: 3 });
    group.add(sleeveMesh);
  }

  // Needle thread on the standing part's top face, bobbin on the tail's
  // underside — the two sides of the same lock stitch through the stack. The
  // fold is on the RIGHT here (the PDP figure's orientation), so the art is
  // used unmirrored: its dense edge already sits toward the loop.
  const sub = parsePaths(pat.paths && pat.paths.paths);
  if (sub.length && dims.patternLengthMm > 0) {
    const th = triBins();
    stitchInto(th, sub, patchPlacer(pat, geom.patch.x0, geom.patch.top, 0.18, 1, false),
      kit.threadFor(pat.needleThreadColor, '#DC2C3E'));
    stitchInto(th, sub, patchPlacer(pat, geom.patch.x0, geom.patch.bottom, 0.18, -1, false),
      kit.threadFor(pat.bobbinThreadColor, '#FFD60A'));
    group.add(meshOf(THREE, th, kit.matFor, { name: 'loop-stitching' }));
  }

  // The key's rows. Nothing is drafted onto the model, so the numbers this end
  // would have carried as dimension lines are handed to the corner block as
  // plain rows and read there, in one place, at rest. Sleeve is conditional on
  // the end actually BEING sleeved — the same `sleeved` the sleeve mesh is —
  // because a naked end has no sleeve length to state.
  const callouts = [
    dimRow('opening', 'Opening', `${dims.openingMm} mm`),
    dimRow('fold', 'Fold', `${dims.foldMm} mm`),
  ];
  const zone = patternDims(pat);
  if (zone) callouts.push(dimRow('pattern', 'Pattern', zone));
  if (sleeved) callouts.push(dimRow('sleeve', 'Sleeve', `${sleeveMm} mm`));

  // The sleeve mesh rides the model out so the panel can offer a Sleeved /
  // Naked toggle (Jerry 2026-08-20): the naked loop is the SAME end minus its
  // tube, so hiding one mesh (and the key's Sleeve row, matched by its
  // data-bcm3d-dim) IS the naked render — no second build, no reframe.
  return { group, callouts, sleeve: sleeveMesh, plate: patternPlate(pat, 'Sewn loop') };
}

function buildJoin(THREE, cfg, kit, pat) {
  const web = cfg.web;
  const dims = patternGeometry(pat, 'join');
  if (!dims) return null;
  const g = {
    thicknessMm: web.thicknessMm,
    overlapMm: dims.overlapMm,
    patternLengthMm: dims.patternLengthMm,
  };
  const geom = joinCentrelines(g);
  const group = new THREE.Group();

  const body = triBins();
  // Each piece runs IN from the line and STOPS inside the overlap, so each has
  // one free end (its outer one, where the drawing stops) and one cut end (its
  // inner one, the real edge the lap is made on). joinCentrelines writes the
  // under piece outer-to-inner and the over piece inner-to-outer, so the two
  // treatments swap between them.
  sweepInto(body, xyFrames(geom.under), kit.web, FREE_SQUARE, CUT_ROUND);
  sweepInto(body, xyFrames(geom.over), kit.web, CUT_ROUND, FREE_SQUARE);
  group.add(meshOf(THREE, body, kit.matFor, { name: 'join-webbing' }));

  const sub = parsePaths(pat.paths && pat.paths.paths);
  if (sub.length && dims.patternLengthMm > 0) {
    const th = triBins();
    stitchInto(th, sub, patchPlacer(pat, geom.patch.x0, geom.patch.top, 0.18, 1, false),
      kit.threadFor(pat.needleThreadColor, '#DC2C3E'));
    stitchInto(th, sub, patchPlacer(pat, geom.patch.x0, geom.patch.bottom, 0.18, -1, false),
      kit.threadFor(pat.bobbinThreadColor, '#FFD60A'));
    group.add(meshOf(THREE, th, kit.matFor, { name: 'join-stitching' }));
  }

  // The join's two, in the same shape and the same order as the loop's: what
  // the lap measures and what the stitching that makes it measures.
  const callouts = [dimRow('overlap', 'Overlap', `${dims.overlapMm} mm`)];
  const zone = patternDims(pat);
  if (zone) callouts.push(dimRow('pattern', 'Pattern', zone));

  return { group, callouts, plate: patternPlate(pat, 'Join stitch') };
}

/* ------------------------------------------------------------- the kit ---- */

/**
 * The sections and materials one config's models share: the webbing's own
 * stadium (carrying the cross-section design), the sleeve, and thread sections
 * minted per colour. One MeshStandardMaterial per material key, however many
 * groups reference it — and one kit per config script, so two products' figures
 * on one page (the batch page's several groups) never borrow each other's
 * colours.
 */
const kitCache = new WeakMap();

function kitFor(THREE, cfg) {
  if (kitCache.has(cfg.tag)) return kitCache.get(cfg.tag);
  const web = cfg.web;
  const cs = readCrossSection(web);

  const std = (h, rough, extra) => new THREE.MeshStandardMaterial(
    Object.assign({ color: new THREE.Color(h), roughness: rough, metalness: 0 }, extra || {})
  );
  const cache = new Map();
  const matFor = (k) => {
    let m = cache.get(k);
    if (m) return m;
    if (k === 'sleeve') {
      // The sleeve: the webbing's own sleeve colour, translucent and with
      // depthWrite off so the webbing reads THROUGH it — the point of drawing
      // it at true length is being able to see what it covers.
      m = std(hex(web.scene && web.scene.sleeveColor, '#000000'), 0.62, {
        transparent: true, opacity: 0.76, depthWrite: false,
      });
    } else if (k.slice(0, 4) === 'thr:') {
      m = std(k.slice(4), 0.5);
    } else {
      m = std(k.slice(4), 0.82);
    }
    cache.set(k, m);
    return m;
  };

  // The sleeve is physically WIDER than the webbing it wraps — the same rule
  // the 2D figures use (28 mm sleeve on 1-inch, 25.4 on 20 mm, proportional
  // below that), so the drawn footprint agrees between the two surfaces.
  const wMm = web.widthMm;
  const sleeveW = wMm >= 23 ? 28 : (wMm >= 18 ? 25.4 : wMm * 1.2);
  const sleeveT = web.thicknessMm + 1.7;

  const threads = new Map();
  const kit = {
    matFor,
    cs,
    web: makeSection(wMm, web.thicknessMm, WEB_SEG,
      webbingDesign(cs, ringDims(wMm, web.thicknessMm))),
    sleeve: makeSection(sleeveW, sleeveT, WEB_SEG,
      plainDesign('sleeve', ringDims(sleeveW, sleeveT))),
    /** A thread section in one colour, minted once per colour per config. */
    threadFor(raw, fallback) {
      const key = threadKey(raw, fallback);
      let s = threads.get(key);
      if (!s) {
        s = makeSection(THREAD_W, THREAD_T, THREAD_SEG,
          plainDesign(key, ringDims(THREAD_W, THREAD_T)));
        threads.set(key, s);
      }
      return s;
    },
  };
  kitCache.set(cfg.tag, kit);
  return kit;
}

/* --------------------------------------------------------------- viewer --- */

/**
 * The opening pose, as bc-model-3d.js states one: `direction` is the model-space
 * vector that ends up pointing at the camera, `up` the one that ends up as
 * screen up. Chosen by eye to put the loop's eye, its stitch patch and its
 * sleeve all in the same glance — a shade above and a shade off-axis, with the
 * webbing running diagonally so a long thin object fits a 16:10 frame.
 */
const VIEW_DIRECTION = [0.1, 0.56, 0.82];
const VIEW_UP = [-0.3, 0.94, 0.06];

/**
 * THE CORNER BLOCK — the drawing's key, and in the full view its title block
 * folded into the same box.
 *
 * A row is what a spec row has always been: the name, muted, hard left; the
 * value, tabular, hard right — the same row shape the written rows below the
 * figure use. INLINE that is all there is. EXPANDED the block grows the title
 * block into itself rather than beside it (pattern code and kind, the pattern's
 * name, the dimension rows, then the stitch count and the two threads with
 * their swatches), because a drawing sheet has ONE corner stamp and two boxes in
 * one corner is a layout, not a stamp. The extra parts are `display: none`
 * behind the `data-bcm3d-expanded` gate, so nothing is built or torn down on the
 * way into or out of the dialog.
 *
 * It goes into the STAGE rather than into the panel around it, for the same
 * reason the hint does: the stage is what gets re-parented into the expand
 * dialog, where the panel is no longer an ancestor of anything and the ink /
 * paper tokens are inverted. Being the last child puts it above the canvas.
 *
 * aria-hidden and pointer-events: none. Every number on the block is in the
 * section's written rows, spelled out in full with its unit conversion, in real
 * markup with real row headers; a screen reader being read the same facts
 * twice, once abbreviated, is worse than not having it.
 */
function keyEl(plate, marks) {
  const el = document.createElement('div');
  el.className = 'bcm3d-key';
  el.setAttribute('aria-hidden', 'true');

  // ONE row shape for both halves of the block — name left, value right, the
  // value carrying a thread swatch where there is one — so the dimension rows
  // and the pattern details read as one table rather than as two lists.
  const row = (parent, label, value, sw) => {
    const r = document.createElement('div');
    r.className = 'bcm3d-key__row';
    const k = document.createElement('span');
    k.className = 'bcm3d-key__label';
    k.textContent = label;
    const v = document.createElement('span');
    v.className = 'bcm3d-key__val';
    if (sw) {
      const chip = document.createElement('span');
      chip.className = 'bcm3d-key__sw';
      chip.style.background = sw;
      v.appendChild(chip);
    }
    v.appendChild(document.createTextNode(value));
    r.append(k, v);
    parent.appendChild(r);
    return r;
  };

  if (plate) {
    const ident = document.createElement('div');
    ident.className = 'bcm3d-key__ident';
    const id = document.createElement('p');
    id.className = 'bcm3d-key__id';
    const code = document.createElement('span');
    code.className = 'bcm3d-key__code';
    code.textContent = plate.code ? `Pattern ${plate.code}` : plate.kind;
    const kind = document.createElement('span');
    kind.className = 'bcm3d-key__kind';
    kind.textContent = plate.code ? plate.kind : '';
    id.append(code, kind);
    const name = document.createElement('p');
    name.className = 'bcm3d-key__name';
    name.textContent = plate.name;
    ident.append(id, name);
    el.appendChild(ident);
  }

  const dims = document.createElement('div');
  dims.className = 'bcm3d-key__dims';
  marks.slice()
    .sort((x, y) => LEGEND_ORDER.indexOf(x.key) - LEGEND_ORDER.indexOf(y.key))
    // The dim key rides the row so panel controls can address one (the
    // Sleeved/Naked toggle hides the Sleeve row with its sleeve mesh).
    .forEach((m) => { row(dims, m.name, m.value).dataset.bcm3dDim = m.key; });
  el.appendChild(dims);

  if (plate && plate.rows.length) {
    const det = document.createElement('div');
    det.className = 'bcm3d-key__rows';
    plate.rows.forEach((r) => row(det, r.k, r.v, r.sw));
    el.appendChild(det);
  }

  return el;
}

function makeViewer(THREE, roomMod, host, model) {
  const stage = host.querySelector('[data-bcm3d-stage]');
  if (!stage) return null;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  } catch (_e) {
    return null; // no WebGL — the poster stays
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  // NO tone mapping, unlike bc-model-3d.js. That viewer shows an anodized
  // aluminium part, where ACES's filmic roll-off is exactly right; this one is
  // a DIAGRAM of a webbing whose colour is a catalog fact, and ACES lifts and
  // desaturates a saturated dye into a colour no webbing has ever been.
  // Straight sRGB out, with the lights kept near unit irradiance so nothing
  // clips instead.
  renderer.toneMapping = THREE.NoToneMapping;
  const canvas = renderer.domElement;
  canvas.className = 'bcm3d-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  // After the poster (which it paints over on the first frame) and before the
  // corner block. The hint is the one stage child left in front of the canvas
  // by z-index rather than by DOM order.
  stage.appendChild(canvas);
  stage.appendChild(keyEl(model.plate, model.callouts || []));

  const scene = new THREE.Scene();
  try {
    // The generator and the room are both throwaways — only the baked texture
    // is kept — so hand their GPU resources back as soon as the bake is done.
    const pmrem = new THREE.PMREMGenerator(renderer);
    const room = new roomMod.RoomEnvironment();
    scene.environment = pmrem.fromScene(room, 0.04).texture;
    pmrem.dispose();
    room.traverse((o) => {
      if (o.geometry) o.geometry.dispose();
      const m = o.material;
      if (Array.isArray(m)) m.forEach((x) => x && x.dispose && x.dispose());
      else if (m && m.dispose) m.dispose();
    });
    scene.environmentIntensity = 0.4;
  } catch (_e) { /* the lights below carry the scene on their own */ }
  scene.add(new THREE.HemisphereLight(0xffffff, 0x6a6a78, 0.55));
  const key = new THREE.DirectionalLight(0xffffff, 0.95);
  key.position.set(60, 90, 120);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.3);
  fill.position.set(-90, -50, -110);
  scene.add(fill);

  const camera = new THREE.PerspectiveCamera(32, 1.6, 1, 4000);
  const pivot = new THREE.Group();
  scene.add(pivot);
  pivot.add(model.group);

  const box = new THREE.Box3().setFromObject(model.group);
  const centre = box.getCenter(new THREE.Vector3());
  model.group.position.sub(centre);
  const size = box.getSize(new THREE.Vector3()).length() || 1;
  const minDist = size * 0.2;
  const maxDist = size * 4;
  const panLimit = size * 0.6;
  camera.near = size / 400;
  camera.far = size * 100;

  const zDir = new THREE.Vector3(...VIEW_DIRECTION).normalize();
  const xDir = new THREE.Vector3(...VIEW_UP).cross(zDir).normalize();
  const yDir = new THREE.Vector3().crossVectors(zDir, xDir);
  const home = new THREE.Quaternion().setFromRotationMatrix(
    new THREE.Matrix4().makeBasis(xDir, yDir, zDir).transpose()
  );
  pivot.quaternion.copy(home);

  // Framing is SOLVED against the opening pose rather than taken as a multiple
  // of the bounding sphere, because both models are long and thin and are shown
  // on the diagonal: a sphere fit (what bc-model-3d.js can afford, its parts
  // being chunky) would leave one of these adrift in the middle of a 16:10
  // panel. Every bounding-box corner is rotated into the home pose and the
  // smallest camera distance that keeps it inside both the horizontal and the
  // vertical frustum is taken, with 6% of air. Tumbling can therefore push a
  // corner out of frame — that is the trade, and it is the right one for a
  // viewer whose first job is to be legible before anyone touches it.
  const corners = [];
  for (let i = 0; i < 8; i += 1) {
    corners.push(new THREE.Vector3(
      i & 1 ? box.max.x : box.min.x,
      i & 2 ? box.max.y : box.min.y,
      i & 4 ? box.max.z : box.min.z
    ).sub(centre).applyQuaternion(home));
  }
  const fit = (aspect) => {
    const tv = Math.tan((camera.fov * Math.PI) / 360);
    const th = tv * Math.max(aspect, 0.2);
    let d = 0;
    corners.forEach((p) => {
      d = Math.max(d, p.z + Math.abs(p.x) / th, p.z + Math.abs(p.y) / tv);
    });
    return Math.max(d * 1.06, size * 0.05);
  };
  let homeDist = fit(camera.aspect);
  let dist = homeDist;
  camera.position.set(0, 0, dist);
  camera.updateProjectionMatrix();

  let dirty = true;
  let rafId = null;
  // Set by dispose() and by nothing else: the context has been handed back, so
  // a frame scheduled after that would draw into nothing. There is no other
  // stop — the panel shows its model for as long as it is on the page, the ×
  // resetting the view rather than putting the model away.
  let dead = false;
  const frame = () => {
    rafId = null;
    if (!dirty) return;
    dirty = false;
    pivot.updateMatrixWorld();
    renderer.render(scene, camera);
  };
  // Render on demand and nothing else: two live WebGL contexts idling at 60 fps
  // for the life of a documentation page would be a rude thing to leave behind.
  const mark = () => { dirty = true; if (!dead && rafId == null) rafId = requestAnimationFrame(frame); };

  const resize = () => {
    const w = stage.clientWidth; const h = stage.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h);
    camera.aspect = w / h;
    // Re-solve the fit for the new shape — and adopt it only if the reader has
    // not zoomed, so a resize (or the move into the expand overlay) never
    // yanks the frame out from under someone who has chosen their own.
    const wasHome = Math.abs(dist - homeDist) < 1e-6;
    homeDist = fit(camera.aspect);
    if (wasHome) { dist = homeDist; camera.position.setLength(dist); }
    camera.updateProjectionMatrix();
    // setSize reallocates (and so clears) the drawing buffer, so the repaint is
    // owed here and now rather than left to the next `mark`.
    dirty = false;
    pivot.updateMatrixWorld();
    renderer.render(scene, camera);
  };
  const ro = new ResizeObserver(resize);
  ro.observe(stage);

  // Put the Sleeved / Naked switch back where it started. Assigned by that
  // switch below when the model has a sleeve to hide, and left a no-op when it
  // has not, so `defaults()` needs no test of its own.
  let sleeveHome = () => {};

  const viewer = {
    host,
    stage,
    canvas,
    armed: false,
    resize,
    /**
     * Hand back everything that outlives the DOM: the observer, and the WebGL
     * context itself. Browsers cap live contexts (~16) and drop the OLDEST
     * when a new one exceeds it, so a theme-editor session that reloads the
     * Sewing section a dozen times would blank the viewers still on screen.
     */
    dispose() {
      ro.disconnect();
      dead = true;
      if (rafId != null) { cancelAnimationFrame(rafId); rafId = null; }
      try { renderer.dispose(); } catch (_e) { /* already gone */ }
      try { renderer.forceContextLoss(); } catch (_e) { /* not supported */ }
    },
    home() {
      pivot.quaternion.copy(home);
      dist = homeDist;
      camera.position.set(0, 0, dist);
      pivot.position.set(0, 0, 0);
      mark();
    },
    /**
     * THE DEFAULT VIEW — the picture the panel showed the moment the model
     * first appeared: the home pose and distance with no pan (`home`), and the
     * sleeve back on. Where the × and Escape land (api.reset).
     *
     * The sleeve is here and NOT in `home()` on purpose. `home()` is also the
     * double-click's job mid-tumble, and that is a request to re-aim the
     * camera, not to undo a Sleeved / Naked choice; and it is what `disarm`
     * calls, so a tap off the panel would otherwise revert a view the reader
     * set and then went to read about.
     */
    defaults() {
      viewer.home();
      sleeveHome();
    },
    zoomBy(f) {
      dist = Math.min(maxDist, Math.max(minDist, dist * f));
      camera.position.setLength(dist);
      mark();
    },
    panBy(dx, dy) {
      const per = (2 * dist * Math.tan((camera.fov * Math.PI) / 360)) / (canvas.clientHeight || 1);
      pivot.position.set(pivot.position.x + dx * per, pivot.position.y - dy * per, 0);
      const off = Math.hypot(pivot.position.x, pivot.position.y);
      if (off > panLimit) pivot.position.multiplyScalar(panLimit / off);
      mark();
    },
    // Free tumble: the MODEL turns about the CAMERA's screen axes, so there is
    // no pole and no upright. make_preview_html.py's premultiply, unchanged.
    tumble(dx, dy) {
      const q = new THREE.Quaternion()
        .setFromAxisAngle(new THREE.Vector3(0, 1, 0), dx)
        .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), dy));
      pivot.quaternion.premultiply(q);
      mark();
    },
  };

  // The Sleeved / Naked switch (Jerry 2026-08-20) — only on loop panels whose
  // webbing offers a sleeve (model.sleeve is the mesh buildLoop drew). The
  // naked view is the SAME model with that one mesh and the key's Sleeve row
  // hidden: no rebuild, no reframe, and flipping back is exact. It lives in
  // the stage like the key, so it travels into the expand dialog; unlike the
  // key it is interactive, so its clicks must not fall through to the host's
  // click-to-arm handler.
  if (model.sleeve) {
    const tgl = document.createElement('div');
    tgl.className = 'bcm3d-toggle';
    tgl.setAttribute('role', 'group');
    tgl.setAttribute('aria-label', 'Loop sleeve');
    const opts = [['Sleeved', false], ['Naked', true]].map(([label, naked]) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bcm3d-toggle__opt';
      b.textContent = label;
      b.setAttribute('aria-pressed', String(!naked));
      b.addEventListener('click', (e) => { e.stopPropagation(); show(naked); });
      return b;
    });
    // One function for both buttons and for the reset, so the × can put the
    // switch back without knowing anything about it (`sleeveHome`).
    const show = (naked) => {
      model.sleeve.visible = !naked;
      opts.forEach((o, i) => o.setAttribute('aria-pressed', String((i === 1) === naked)));
      // A naked end has no sleeve length to state — the same rule buildLoop
      // applies when the webbing never offers one.
      const rowEl = stage.querySelector('.bcm3d-key__row[data-bcm3d-dim="sleeve"]');
      if (rowEl) rowEl.hidden = naked;
      mark();
    };
    sleeveHome = () => show(false);
    tgl.append(...opts);
    stage.appendChild(tgl);
  }

  resize();
  // Flagged on BOTH: the host styles the panel chrome it keeps (the expand
  // button, the ×, the cursor), the stage styles what travels with it into the
  // dialog, where the host is no longer an ancestor of anything.
  //
  // ONE FLAG — "there is a viewer here, and it is the picture on screen". Set
  // after the first frame, never removed: the canvas fades up over the poster
  // and stays up, and the × resets the view rather than taking this off again
  // (api.reset). `armed` is the only state on top of it, and that one is the
  // reader's grip on the model, not which picture is showing.
  host.dataset.bcm3dBuilt = '';
  stage.dataset.bcm3dBuilt = '';
  return viewer;
}

/* -------------------------------------------------------------- gestures -- */

function wire(viewer, api) {
  const canvas = viewer.canvas;
  const pointers = new Map();
  let pinch = 0; let midX = 0; let midY = 0;
  let panDrag = false;

  const spread = () => {
    const [a, b] = [...pointers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  const mid = () => {
    const [a, b] = [...pointers.values()];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  };
  const panning = (e) => {
    panDrag ||= Boolean(e.buttons & 2) || e.shiftKey;
    return panDrag;
  };

  canvas.addEventListener('pointerdown', (e) => {
    if (!viewer.armed) return;
    canvas.setPointerCapture(e.pointerId);
    if (pointers.size === 0) panDrag = false;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    panning(e);
    if (pointers.size === 2) { pinch = spread(); ({ x: midX, y: midY } = mid()); }
  });

  canvas.addEventListener('pointermove', (e) => {
    if (!viewer.armed || !pointers.has(e.pointerId)) return;
    const prev = pointers.get(e.pointerId);
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size >= 2) {
      const next = spread(); const m = mid();
      if (pinch > 0 && next > 0) viewer.zoomBy(pinch / next);
      pinch = next;
      viewer.panBy(m.x - midX, m.y - midY);
      midX = m.x; midY = m.y;
      return;
    }
    if (panning(e)) { viewer.panBy(e.clientX - prev.x, e.clientY - prev.y); return; }
    const s = 3.2 / (canvas.clientHeight || 1);
    viewer.tumble((e.clientX - prev.x) * s, (e.clientY - prev.y) * s);
  });

  const release = (e) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = 0;
    if (pointers.size === 0) panDrag = false;
  };
  canvas.addEventListener('pointerup', release);
  canvas.addEventListener('pointercancel', release);
  canvas.addEventListener('contextmenu', (e) => { if (viewer.armed) e.preventDefault(); });
  canvas.addEventListener('dblclick', (e) => {
    if (viewer.armed) { e.preventDefault(); viewer.home(); }
  });

  // Not passive, but only ever preventing the default once ARMED: an inert
  // viewer is a picture, and the page scrolls straight over it.
  canvas.addEventListener('wheel', (e) => {
    if (!viewer.armed) return;
    e.preventDefault();
    viewer.zoomBy(Math.exp(e.deltaY * 0.0007));
  }, { passive: false });

  viewer.host.addEventListener('click', (e) => {
    if (e.target.closest('[data-bcm3d-expand]')) { e.preventDefault(); api.expand(viewer); return; }
    // Back to the default view. Tested BEFORE the arm branch, and it ends
    // here: a click that lands on the × must not be read as a click on the
    // panel and re-arm what it just put down.
    if (e.target.closest('[data-bcm3d-exit]')) { e.preventDefault(); api.reset(viewer); return; }
    if (!viewer.armed) api.arm(viewer);
  });
}

/* --------------------------------------------------------- manifold check -- */

/**
 * `window.bcSewing3d.check()` — are the solids closed?
 *
 * Directed-edge matching, the cheapest honest test there is: in a closed,
 * consistently wound surface every directed edge a->b is answered by exactly
 * one b->a on the neighbouring triangle. An unanswered edge is a hole; an edge
 * answered in the SAME direction is a flipped triangle. Both would show up in
 * the render as a black facet or a see-through end.
 *
 * Three accommodations, none of them a fudge. Vertices are quantised to a
 * micron because the cap face and the profile ring reach the same point by
 * different arithmetic, which agrees to about 1e-7 mm and not to the bit. The
 * cut-face decals — the cross-section's pill and edge circles, lifted proud of
 * the face — are genuinely open patches ON the solid rather than part of it, so
 * they are flagged at build time (triBins.decal) and counted separately. And
 * the test is BALANCE, count(a->b) == count(b->a), not "exactly one each way":
 * the stitching is hundreds of separate closed tubes, and wherever two collinear
 * stitches meet, one tube's end cap sits exactly on the next one's start cap.
 * Two solids touching face to face is a doubled but balanced edge, which is
 * what `coincident` counts; a hole or a flipped winding is an UNBALANCED one,
 * and that is the number that has to be zero.
 */
function manifoldReport(part) {
  const pos = part.geo.getAttribute('position').array;
  const tris = pos.length / 9;
  const key = (i) => `${Math.round(pos[i] * 1e3)},${Math.round(pos[i + 1] * 1e3)},${Math.round(pos[i + 2] * 1e3)}`;
  const edges = new Map();
  let skipped = 0;
  let degenerate = 0;
  for (let t = 0; t < tris; t += 1) {
    if (part.dec && part.dec[t]) { skipped += 1; continue; }
    const i = t * 9;
    const v = [key(i), key(i + 3), key(i + 6)];
    if (v[0] === v[1] || v[1] === v[2] || v[2] === v[0]) { degenerate += 1; continue; }
    for (let e = 0; e < 3; e += 1) {
      const a = v[e];
      const b = v[(e + 1) % 3];
      const k = `${a}|${b}`;
      edges.set(k, (edges.get(k) || 0) + 1);
    }
  }
  let open = 0;
  let unbalanced = 0;
  let coincident = 0;
  const where = [];
  edges.forEach((n, k) => {
    const [a, b] = k.split('|');
    const back = edges.get(`${b}|${a}`) || 0;
    if (back === n) { coincident += n - 1; return; }
    unbalanced += 1;
    if (back === 0) open += 1;
    if (where.length < 4) where.push(`${a} -> ${b} (${n}/${back})`);
  });
  return {
    part: part.name,
    triangles: tris,
    decals: skipped,
    degenerate,
    openEdges: open,
    unbalanced,
    coincident,
    watertight: unbalanced === 0,
    where: where.join('  |  '),
  };
}

/* ----------------------------------------------------------------- main --- */

const viewers = [];
const debug = {
  viewers,
  parts: PARTS,
  loops: [],
  check() {
    const rows = PARTS.map(manifoldReport);
    if (console.table) console.table(rows);
    return { watertight: rows.every((r) => r.watertight), rows };
  },
};
// The module's ONE global: a debug handle, nothing the page depends on.
window.bcSewing3d = debug;

/**
 * three.js, fetched once for the whole page (a PDP carries a panel per sewing
 * design, a batch record one per strength group).
 *
 * A REJECTION IS NOT CACHED. `||=` on the bare `Promise.all` would keep a
 * rejected promise forever, so one blocked or flaky jsDelivr fetch — an ad
 * blocker, a captive portal, a CDN blip on the first press — would hand every
 * LATER press an instant failure, and the whole page's panels would go inert
 * on the strength of one bad moment. Clearing the handle on the way out means
 * the next press tries again; the panel that took the failure keeps its own
 * `fail()` result, which is the deliberate part (see claim()).
 */
let libPromise = null;
const loadLib = () => (libPromise ||= Promise.all(MODULE_URLS.map((u) => import(u)))
  .catch((e) => { libPromise = null; throw e; }));

let dialog = null;
let expanded = null;
const started = new WeakSet();

/**
 * There will be no model here: no config, no pattern, no WebGL, no three.js,
 * or geometry the data cannot make. The poster is the picture from now on, so
 * the promises come back OFF with the same call — `js` (the pointer cursor and
 * "Click to interact") and any press left mid-flight — and `failed` goes on for
 * anything that wants to style the dead end.
 */
const fail = (host) => {
  host.dataset.bcm3dFailed = '';
  delete host.dataset.bcm3dJs;
  delete host.dataset.bcm3dLoading;
  const st = host.querySelector('[data-bcm3d-stage]');
  if (st) {
    st.dataset.bcm3dFailed = '';
    delete st.dataset.bcm3dJs;
    delete st.dataset.bcm3dLoading;
  }
};

const api = {
  arm(v) {
    viewers.forEach((o) => { if (o !== v) api.disarm(o); });
    v.armed = true;
    v.host.dataset.bcm3dArmed = '';
    v.stage.dataset.bcm3dArmed = '';
    v.canvas.style.touchAction = 'none';
  },
  disarm(v) {
    if (!v.armed) return;
    v.armed = false;
    delete v.host.dataset.bcm3dArmed;
    delete v.stage.dataset.bcm3dArmed;
    v.canvas.style.touchAction = '';
    v.home();
  },
  /**
   * BACK TO THE DEFAULT VIEW — the × and Escape, and nothing else. (A tap off
   * the panel only DISARMS: releasing the wheel is one thing, and re-aiming a
   * model someone has set down beside a spec table to read is another.)
   *
   * The model STAYS. This used to fade the flat pattern art back over the
   * canvas, and that was the wrong answer to the question the × asks: the art
   * is a drawing of the stitching, the panel is about the sewn end, so "done
   * turning this over" landed the reader on a different subject (Jerry
   * 2026-08-24). What it hands back instead is the picture the panel opened
   * with — home pose, home distance, no pan, sleeve on — with the gestures
   * released, so the page scrolls over it again and the corner ⤢ / × are the
   * pair they were before anyone touched it.
   *
   * The flat art keeps its one job: the poster, for the panel that has no
   * model yet and the panel that can never have one (no WebGL, no CDN, a
   * pattern with no geometry). Nothing takes `data-bcm3d-built` off again.
   */
  reset(v) {
    api.disarm(v);   // hands the page its scroll back, and homes the pose with it
    v.defaults();    // …and the sleeve, so this is the opening picture exactly
  },
  expand(v) {
    ensureDialog();
    dialog.querySelector('[data-bcm3d-dialog-stage]').appendChild(v.stage);
    // The full view is where the key GROWS the title block into itself. One
    // flag, on the stage, because the stage is the thing that moves.
    v.stage.dataset.bcm3dExpanded = '';
    dialog.showModal();
    api.arm(v);
    expanded = v;
    requestAnimationFrame(() => v.resize());
  },
};

/**
 * COMING BACK FROM THE FULL VIEW.
 *
 * The stage lives IN the dialog while expanded, so if the restore does not run
 * the inline panel is left EMPTY — canvas, poster, key and all — until the page
 * is reloaded. Hanging that on the dialog's `close` event alone is exactly the
 * fragile version: `dialog.close()` clears the element's open state and, in
 * some environments, dispatches nothing.
 *
 * So the restore is its own IDEMPOTENT function — `expanded` is both the state
 * and the guard, cleared before anything moves — which is what lets every close
 * path call it without coordinating: the × button calls it directly, the Escape
 * fallback calls it, the `close` listener still calls it where that event does
 * fire, and a MutationObserver on the dialog's own `open` attribute catches
 * every other way a dialog can be dismissed (light-dismiss, the close watcher,
 * anything else calling `dialog.close()`). Whichever gets there first does the
 * work; the rest are no-ops.
 *
 * The `dialog.open` guard is the other half of that: `dialog.close()` clears
 * the open state SYNCHRONOUSLY but queues its `close` event, and a throttled
 * tab can hold that task until after the reader has expanded again — at which
 * point a stale event would send the LIVE stage home and leave an empty dialog
 * on screen (seen in testing). Every deliberate path closes the dialog before
 * calling here, so the guard costs them nothing and rejects exactly the stale
 * ones. `cancel` (which precedes the close) is one of those: it is rejected
 * here and the close it leads to is caught by the observer a moment later.
 */
const collapse = () => {
  const v = expanded;
  if (!v) return;
  if (dialog && dialog.open) return;
  expanded = null;
  delete v.stage.dataset.bcm3dExpanded;
  v.host.insertBefore(v.stage, v.host.firstChild);
  // Disarm on the way out (which homes the pose too): a viewer left live
  // inline is a viewer that eats the next scroll.
  api.disarm(v);
  requestAnimationFrame(() => v.resize());
};

function ensureDialog() {
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'bcm3d-dialog';
  dialog.innerHTML = '<div class="bcm3d-dialog__stage" data-bcm3d-dialog-stage></div>'
    + '<button class="bcm3d-dialog__close" type="button" data-bcm3d-close '
    + 'aria-label="Close 3D preview">×</button>';
  document.body.appendChild(dialog);

  // The catch-all, and the only one of these that cannot be routed around: the
  // `open` attribute is what "this dialog is showing" IS, so every dismissal
  // ends here whether or not it fired an event. (showModal lands here too and
  // is a no-op, since the guard is `!dialog.open`.)
  new MutationObserver(() => { if (!dialog.open) collapse(); })
    .observe(dialog, { attributes: true, attributeFilter: ['open'] });

  // Belt: kept for the environments where `close` does fire, and harmless in
  // the ones where the observer got there first.
  dialog.addEventListener('close', collapse);
  // Escape, the dialog's own way out: `cancel` precedes the close, and the
  // default is not prevented, so the dialog still closes itself.
  dialog.addEventListener('cancel', collapse);
  // A click that lands on the dialog ELEMENT rather than on anything inside it
  // is a backdrop click. `.bcm3d-dialog__stage` is inset: 0, so today this can
  // only happen under a browser's own light-dismiss; it is here so that path
  // goes home too rather than leaving the panel empty.
  dialog.addEventListener('click', (e) => {
    if (e.target !== dialog) return;
    dialog.close();
    collapse();
  });
  // Braces: close AND restore, in that order, without waiting to be told.
  dialog.querySelector('[data-bcm3d-close]').addEventListener('click', () => {
    dialog.close();
    collapse();
  });
  return dialog;
}

let documentWired = false;

function wireDocument() {
  if (documentWired) return;
  documentWired = true;
  // Tapping off the viewers hands the page its scroll back, exactly as
  // bc-model-3d.js does; Escape is the keyboard's version of the same.
  document.addEventListener('click', (e) => {
    if (expanded || (e.target.closest && e.target.closest('[data-bcm3d-host]'))) return;
    viewers.forEach((v) => api.disarm(v));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // Expanded, Escape is the full view's way out — handled HERE as well as by
    // the dialog's own close watcher, because the watcher's events are exactly
    // what cannot be relied on. Both orders work: whichever runs second finds
    // `expanded` already null and does nothing. (This listener runs BEFORE the
    // UA processes the close request, so `expanded` is still set: the branch
    // above is what keeps Escape-out-of-the-overlay landing on the inline 3D
    // panel rather than carrying on to reset the panel underneath.)
    if (expanded) { dialog.close(); collapse(); return; }
    // Inline, Escape is the keyboard's ×: back to the default view.
    //
    // ARMED PANELS ONLY. An Escape is a page-wide signal that might have been
    // meant for a drawer or a menu, and a record can carry a model per strength
    // group; armed is the reader having said this panel is theirs, and that is
    // the one that goes back.
    viewers.forEach((v) => { if (v.armed) api.reset(v); });
  });
}

/**
 * Build one host's viewer once three.js is in hand. Returns it, or null when
 * there was nothing to build — the caller arms what it gets back.
 */
function build(THREE, roomMod, host, cfg, pick) {
  let model = null;
  try {
    const kit = kitFor(THREE, cfg);
    model = pick.kind === 'join'
      ? buildJoin(THREE, cfg, kit, pick.pat)
      : buildLoop(THREE, cfg, kit, pick.pat);
  } catch (_e) {
    model = null;                        // malformed data: keep the poster
  }
  if (!model) { fail(host); return null; }

  // makeViewer gets a try of its own: it appends its canvas to the stage
  // BEFORE the parts that can throw (a three.js version drift, a WebGL call
  // the driver refuses), and a throw here escapes `claim`'s `.then(ok, err)`
  // as an unhandled rejection — the error handler is the SIBLING of this
  // callback and never sees it. The reader would be left with a blank canvas
  // painted over the still the poster was there to keep.
  let v = null;
  try {
    v = makeViewer(THREE, roomMod, host, model);
  } catch (_e) {
    v = null;
  }
  if (!v) {
    host.querySelectorAll('[data-bcm3d-stage] > .bcm3d-canvas').forEach((c) => c.remove());
    fail(host);
    return null;
  }
  viewers.push(v);
  wire(v, api);
  wireDocument();
  return v;
}

/**
 * Claim one host: resolve its config and pattern, then WAIT TO BE ASKED.
 *
 * TAP TO LOAD (Jerry 2026-08-24, the page-load complaint). three.js is ~600 KB
 * of module and the model is made after it lands; a documentation page can
 * carry one of these per sewing design and a record one per strength group, so
 * nothing is fetched until the reader presses the panel — the same bargain the
 * batch record's rig figure strikes (snippets/bc-batch-rig-figure.liquid), and
 * the reason the poster is now a STILL OF THE DEFAULT VIEW rather than the
 * pattern's flat art: what stands in for the model has to be a picture of it.
 *
 * (It used to build on IntersectionObserver, 400 px before the panel was
 * reached. That is what made scrolling past a PDP's Sewing section cost a
 * three.js download and two WebGL contexts nobody asked for.)
 *
 * One press does BOTH — fetch and arm — because that is what the press meant.
 * The load is shared: two hosts on one page fetch three.js once. A host the
 * module cannot build from never gets `js` at all, so its poster is simply the
 * picture, exactly as with no JS.
 */
function claim(host) {
  if (started.has(host)) return;
  started.add(host);

  const cfg = configFor(host);
  const pick = cfg && patternFor(cfg, host);
  const stage = host.querySelector('[data-bcm3d-stage]');
  if (!cfg || !pick || !stage) { fail(host); return; }

  // The promise, made only now that it is good: the cursor turns, and the hint
  // switches from "3D preview" to "Click to interact".
  host.dataset.bcm3dJs = '';
  stage.dataset.bcm3dJs = '';

  let pressed = false;
  const go = () => {
    if (pressed) return;
    pressed = true;
    host.dataset.bcm3dLoading = '';
    stage.dataset.bcm3dLoading = '';
    loadLib().then(
      ([THREE, roomMod]) => {
        delete host.dataset.bcm3dLoading;
        delete stage.dataset.bcm3dLoading;
        const v = build(THREE, roomMod, host, cfg, pick);
        // The press asked for the model AND for the handle on it. Nothing else
        // arms a viewer at birth, which is why this is here and not in build().
        if (v) api.arm(v);
      },
      // No network / no module support: the stage keeps the picture it shipped
      // with, which is the whole reason that poster is in the page.
      () => fail(host)
    );
  };

  // MOUSE pointerdown starts the fetch on the way down (the click that follows
  // is caught by the panel's own handler, wire(), which arms). Touch is click
  // only: pointerdown fires at touchstart, before the browser knows whether the
  // finger is tapping or scrolling, and a scroll that merely starts on a panel
  // must not download three.js, build the model and arm it — which would then
  // capture the next scroll as a tumble. That is the rig figure's contract too.
  host.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button === 0) go();
  });
  host.addEventListener('click', go);
}

/**
 * Scan for hosts. Idempotent by construction — a host already claimed is in
 * `started` and is skipped — so it is safe to run again when the theme editor
 * re-renders a section.
 */
function init() {
  // Reap first. A theme-editor section reload REPLACES the host element, so the
  // viewer built on the old one is unreachable but still holds a live WebGL
  // context and a ResizeObserver, and `viewers` would keep handing it to every
  // disarm-all sweep. Backwards, because entries are spliced out in place.
  for (let i = viewers.length - 1; i >= 0; i -= 1) {
    const v = viewers[i];
    // The expanded viewer's stage lives in the dialog, not in its host — never
    // tear that one down under the reader.
    if (v === expanded || v.host.isConnected) continue;
    try { v.dispose(); } catch (_e) { /* nothing left to give back */ }
    viewers.splice(i, 1);
  }
  document.querySelectorAll('[data-bcm3d-host]').forEach(claim);
}

init();
document.addEventListener('shopify:section:load', init);
